/**
 * MapLegend — what the pin colours mean, for the layers on the map now.
 *
 * Heritage pins are coloured by period and legends by category, so one dot per
 * layer would say something false; the legend lists the colours that are
 * actually drawn, grouped by layer. Opened from a disclosure button in the
 * status bar (App.tsx), which is desktop-only, and built from the same period,
 * category and layer colours the map's paint expressions use.
 *
 * Added 26 Sep 2026: the map had no key at all, and a first-time visitor saw
 * purple, green and gold pins with nothing saying why.
 */
import { useState } from "react";
import { Palette } from "lucide-react";
import type { Lang } from "../types/index.ts";
import { t } from "../i18n/index.ts";
import styles from "./MapLegend.module.css";
import nav from "./NavBtn.module.css";

export interface LegendItem { label: string; color: string }
export interface LegendSection { title?: string; items: LegendItem[] }

/**
 * `text` is the status bar's «Farver» toggle on a wide screen; `icon` is a
 * round button in the profile stack for a phone, which has no status bar.
 * The id differs so both can be in the page at once.
 */
export function MapLegend({ sections, lang, variant = "text" }: { sections: LegendSection[]; lang: Lang; variant?: "text" | "icon" }) {
  const [open, setOpen] = useState(false);
  const panelId = variant === "icon" ? "map-legend-phone" : "map-legend";
  const shown = sections.filter((s) => s.items.length > 0);
  if (!shown.length) return null;
  return (
    <div className={variant === "icon" ? styles.wrapIcon : styles.wrap}>
      {variant === "icon" ? (
        <button type="button" className={`${nav.btn} ${nav.btnIcon}${open ? ` ${nav.btnOn}` : ""}`} aria-expanded={open} aria-controls={panelId} aria-label={t("legend.title", lang)} title={t("legend.title", lang)} onClick={() => setOpen((v) => !v)}>
          <Palette size={18} />
        </button>
      ) : (
        <button type="button" className={styles.toggle} aria-expanded={open} aria-controls={panelId} onClick={() => setOpen((v) => !v)}>
          {t("legend.title", lang)}
        </button>
      )}
      {open && (
        // Focusable and named because it scrolls when many layers are on, and a
        // scrolling box with nothing to tab to is out of a keyboard's reach.
        <div id={panelId} className={variant === "icon" ? `${styles.panel} ${styles.panelIcon}` : styles.panel} role="region" aria-label={t("legend.title", lang)} tabIndex={0}>
          {shown.map((s, i) => (
            <div key={s.title ?? i} className={styles.section}>
              {s.title && <div className={styles.heading}>{s.title}</div>}
              <ul className={styles.list}>
                {s.items.map((it) => (
                  <li key={it.label} className={styles.item}>
                    <span className={styles.dot} style={{ background: it.color }} aria-hidden="true" />
                    {/* Some labels are the status bar's lower-case counts ("runesten"). */}
                    {it.label.charAt(0).toLocaleUpperCase() + it.label.slice(1)}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
